import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, Search, X } from 'lucide-react';
import { motion, useDragControls } from 'framer-motion';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { PlaybookTransformItem, PlaybookEventConfig } from '@/types/playbook';
import { EditorSelection } from './PlaybookEditorTab';
import MappingProgressBar from './MappingProgressBar';
import { fetchAllSchemaDefinitions } from '@/services/schemaDefinitionApiService';
import { SchemaDefinition } from '@/types/contract';
import { Parameter, EventSchema, UnifiedSchema } from '@/types/schema';
import SchemaTreeView from './SchemaTreeView';
import {
  SchemaProperty,
  convertToParameters,
  enhancedCompareSchemas,
  updateSchemaPropertiesWithComparison,
  SchemaComparisonResult,
  getMappedFieldCounts,
  getSchemaFieldCounts,
  isTransformProperlyConfigured
} from '@utils/playbookSchemaUtils';
import { exportPlaybookAndConnectionYaml } from '@/utils/playbookUtils';
import { PlaybookEditState } from '@/pages/labs/PlaybookEditPage';
import YamlCodeViewer from './YamlCodeViewer';

interface PlaybookSchemaPreviewPanelProps {
  state: PlaybookEditState;
  inputSchemaId?: string;
  outputSchemaId?: string;
  transforms: PlaybookTransformItem[];
  events: PlaybookEventConfig[];
  selection: EditorSelection;
  baselineSchemaEvents: EventSchema[];
  width: number;
  setWidth: React.Dispatch<React.SetStateAction<number>>;
  baselineSchema?: 'input' | 'output';
  inputSchema?: UnifiedSchema | null;
  outputSchema?: UnifiedSchema | null;
  isDesktopMedium?: boolean;
  isLargeDesktop?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  onTransformSelect: (transformId: string) => void;
  onAddTransforms?: (transforms: PlaybookTransformItem[]) => void;
  currentSchemaView: 'input' | 'output' | 'yaml';
  setCurrentSchemaView: React.Dispatch<React.SetStateAction<'input' | 'output' | 'yaml'>>;
  currentYamlView: 'playbook' | 'connection';
  setCurrentYamlView: React.Dispatch<React.SetStateAction<'playbook' | 'connection'>>;
}

const PlaybookSchemaPreviewPanel: React.FC<PlaybookSchemaPreviewPanelProps> = ({
  state,
  inputSchemaId,
  outputSchemaId,
  transforms,
  events,
  selection,
  baselineSchemaEvents,
  width,
  setWidth,
  baselineSchema,
  inputSchema,
  outputSchema,
  isLargeDesktop = false,
  isCollapsed = false,
  onTransformSelect,
  onAddTransforms,
  currentSchemaView,
  setCurrentSchemaView,
  currentYamlView,
  setCurrentYamlView,
}) => {
  // Using underscore prefix to indicate intentionally unused state setter
  const [_, setSchemas] = useState<SchemaDefinition[]>([]);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [filterStatus, setFilterStatus] = useState<'all' | 'mapped' | 'missing' | 'extra' | 'mismatch' | 'incomplete'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [derivedSchema, setDerivedSchema] = useState<SchemaProperty[]>([]);
  const [treeParameters, setTreeParameters] = useState<Parameter[]>([]);
  // Using underscore prefix to indicate intentionally unused state variable
  const [_inputSchemaData, setInputSchemaData] = useState<SchemaDefinition | null>(null);
  const [outputSchemaData, setOutputSchemaData] = useState<SchemaDefinition | null>(null);
  const [generatedYaml, setGeneratedYaml] = useState('');

  const getFilterCount = useCallback((status: string) => {
    return derivedSchema.filter(p => p.status === status).length;
  }, [derivedSchema]);

  // Calculate mapping progress for the baseline schema within this component
  const mappingProgress = useMemo(() => {
    let currentSchema: UnifiedSchema | undefined;
    let isInputBaseline = false;

    if (baselineSchema === 'input' && inputSchema) {
      currentSchema = inputSchema;
      isInputBaseline = true;
    } else if (baselineSchema === 'output' && outputSchema) {
      currentSchema = outputSchema;
      isInputBaseline = false;
    }

    if (!currentSchema) {
      return { 
        mappedCount: 0, 
        totalCount: 0, 
        requiredMappedCount: 0, 
        requiredTotalCount: 0,
        missingCount: 0,
        extraCount: 0,
        mismatchCount: 0
      };
    }

    const { totalCount, requiredTotalCount } = getSchemaFieldCounts(currentSchema);
    const { mappedCount, requiredMappedCount } = getMappedFieldCounts(currentSchema, transforms, isInputBaseline);

    // Calculate additional counts for the progress bar segments
    const missingCount = getFilterCount('missing');
    const extraCount = getFilterCount('extra');
    const mismatchCount = getFilterCount('mismatch');

    return { 
      mappedCount, 
      totalCount, 
      requiredMappedCount, 
      requiredTotalCount,
      missingCount,
      extraCount,
      mismatchCount
    };
  }, [baselineSchema, transforms, inputSchema, outputSchema, getFilterCount, derivedSchema]);

  useEffect(() => {
    if (currentSchemaView === 'yaml' && state) {
      try {
        // Use the unified export function to get clean YAML
        const { playbookYaml, connectionYaml } = exportPlaybookAndConnectionYaml(state);

        let combinedYaml = '';
        if (currentYamlView === 'playbook') {
          combinedYaml = playbookYaml;
        } else {
          combinedYaml = connectionYaml;
        }
        setGeneratedYaml(combinedYaml);
      } catch (error: any) {
        console.error('Failed to generate YAML:', error);
        const errorYaml = `# Error generating YAML preview\n# ${error.message || 'Unknown error occurred'}\n\n# Please check your playbook configuration and try again.`;
        setGeneratedYaml(errorYaml);
      }
    }
  }, [state, currentSchemaView, currentYamlView]);

  const deriveOutputSchema = useCallback((currentTransforms: PlaybookTransformItem[]): SchemaProperty[] => {
    const properties: SchemaProperty[] = [];
    
    // Process transforms to derive output properties
    currentTransforms.forEach(transform => {
      const sourceEventId = transform.appliesToScope === 'eventSpecific' && transform.eventIds && transform.eventIds.length > 0
        ? transform.eventIds[0]
        : undefined;

      const isProperlyConfiguredTransform = isTransformProperlyConfigured(transform);

      switch (transform.type) {
        case 'mapping':
          const mappingContent = transform.content as any;
          if (mappingContent.outputKey && isProperlyConfiguredTransform) {
              properties.push({
                path: mappingContent.outputKey,
                dataType: transform.outputType || 'string', 
                description: undefined, 
                status: 'mapped',
                source: transform.name || `Mapping`, 
                sourceTransformType: transform.type, 
                sourceTransformId: transform.id, 
                sourceEventId: sourceEventId,
                sourceInputPath: mappingContent.inputKey,
                sourceOutputPath: mappingContent.outputKey,
                sourceAppliesToScope: transform.appliesToScope,
              });
          } else if (mappingContent.outputKey && !isProperlyConfiguredTransform) {
            // Transform has output key but is missing input key - mark as incomplete
            properties.push({
              path: mappingContent.outputKey,
              dataType: transform.outputType || 'string',
              description: 'Incomplete mapping transform - missing input key',
              status: 'incomplete',
              source: `${transform.name || 'Mapping'} (Incomplete)`,
              sourceTransformType: transform.type,
              sourceTransformId: transform.id,
              sourceEventId: sourceEventId,
              sourceInputPath: mappingContent.inputKey,
              sourceOutputPath: mappingContent.outputKey,
              sourceAppliesToScope: transform.appliesToScope,
            });
          }
          break;
        case 'enrichment':
          const enrichmentContent = transform.content as any;
          if (enrichmentContent.outputKey && isProperlyConfiguredTransform) {
            properties.push({
              path: enrichmentContent.outputKey,
              dataType: getEnrichmentDataType(enrichmentContent.type),
              description: `Enriched with ${enrichmentContent.type}`,
              status: 'mapped',
              source: transform.name || `Enrichment`,
              sourceTransformType: transform.type,
              sourceTransformId: transform.id,
              sourceEventId: sourceEventId,
              sourceInputPath: undefined,
              sourceOutputPath: enrichmentContent.outputKey,
              sourceAppliesToScope: transform.appliesToScope,
            });
          } else if (enrichmentContent.outputKey && !isProperlyConfiguredTransform) {
            // Transform has output key but is not properly configured
            properties.push({
              path: enrichmentContent.outputKey,
              dataType: getEnrichmentDataType(enrichmentContent.type),
              description: 'Incomplete enrichment transform - missing configuration',
              status: 'incomplete',
              source: `${transform.name || 'Enrichment'} (Incomplete)`,
              sourceTransformType: transform.type,
              sourceTransformId: transform.id,
              sourceEventId: sourceEventId,
              sourceInputPath: undefined,
              sourceOutputPath: enrichmentContent.outputKey,
              sourceAppliesToScope: transform.appliesToScope,
            });
          }
          break;
        case 'expression':
          const expressionContent = transform.content as any;
          if (expressionContent.declaredOutputProperties && isProperlyConfiguredTransform) {
            expressionContent.declaredOutputProperties.forEach((prop: any) => {
              properties.push({
                path: prop.path,
                dataType: prop.dataType,
                description: prop.description || 'From expression',
                status: 'mapped',
                source: transform.name || 'Expression',
                sourceTransformType: transform.type,
                sourceTransformId: transform.id,
                sourceEventId: sourceEventId,
                sourceInputPath: undefined,
                sourceOutputPath: prop.path,
                sourceAppliesToScope: transform.appliesToScope,
              });
            });
          } else if (expressionContent.declaredOutputProperties && !isProperlyConfiguredTransform) {
            // Transform has output properties but is not properly configured
            expressionContent.declaredOutputProperties.forEach((prop: any) => {
              properties.push({
                path: prop.path,
                dataType: prop.dataType,
                description: 'Incomplete expression transform - missing configuration',
                status: 'incomplete',
                source: `${transform.name || 'Expression'} (Incomplete)`,
                sourceTransformType: transform.type,
                sourceTransformId: transform.id,
                sourceEventId: sourceEventId,
                sourceInputPath: undefined,
                sourceOutputPath: prop.path,
                sourceAppliesToScope: transform.appliesToScope,
              });
            });
          }
          break;
        case 'spreading':
          const spreadingContent = transform.content as any;
          if (spreadingContent.inferredOutputProperties && isProperlyConfiguredTransform) {
            spreadingContent.inferredOutputProperties.forEach((prop: any) => {
              properties.push({
                path: prop.path,
                dataType: prop.dataType,
                description: prop.description || 'From spreading',
                status: 'mapped',
                source: transform.name || `Spreading`,
                sourceTransformType: transform.type,
                sourceTransformId: transform.id,
                sourceEventId: sourceEventId,
                sourceInputPath: spreadingContent.inputKey,
                sourceOutputPath: prop.path,
                sourceAppliesToScope: transform.appliesToScope,
              });
            });
          } else if (spreadingContent.inferredOutputProperties && !isProperlyConfiguredTransform) {
            // Transform has output properties but is missing input key
            spreadingContent.inferredOutputProperties.forEach((prop: any) => {
              properties.push({
                path: prop.path,
                dataType: prop.dataType,
                description: 'Incomplete spreading transform - missing input key',
                status: 'incomplete',
                source: `${transform.name || 'Spreading'} (Incomplete)`,
                sourceTransformType: transform.type,
                sourceTransformId: transform.id,
                sourceEventId: sourceEventId,
                sourceInputPath: spreadingContent.inputKey,
                sourceOutputPath: prop.path,
                sourceAppliesToScope: transform.appliesToScope,
              });
            });
          }
          break;
        // Note: Filter transforms are not included in schema preview as they are modifiers/rules
        // that determine data flow, not actual data properties in the output schema
        case 'filter':
          // Filters don't contribute to the output schema structure
          break;
      }
    });

    return properties;
  }, [isTransformProperlyConfigured]); // Add isTransformProperlyConfigured to dependencies

  const getEnrichmentDataType = useCallback((enrichmentType: string): string => {
    switch (enrichmentType) {
      case 'Date':
      case 'Timestamp':
        return 'string';
      case 'UUID':
        return 'string';
      case 'PseudoRandomValue':
        return 'string';
      default:
        return 'string';
    }
  }, []); // No dependencies

  const dragControls = useDragControls();

  useEffect(() => {
    const loadSchemas = async () => {
      try {
        const schemasData = await fetchAllSchemaDefinitions();
        setSchemas(schemasData);
        
        // Set input and output schema data
        if (inputSchemaId) {
          const inputSchema = schemasData.find(s => s.id === inputSchemaId);
          if (inputSchema) {
            setInputSchemaData(inputSchema);
          }
        }
        
        if (outputSchemaId) {
          const outputSchema = schemasData.find(s => s.id === outputSchemaId);
          if (outputSchema) {
            setOutputSchemaData(outputSchema);
          }
        }
      } catch (error) {
        console.error('Failed to load schemas:', error);
      }
    };
    loadSchemas();
  }, [inputSchemaId, outputSchemaId]);

  useEffect(() => {
    // Use currentSchemaView to respect user's selection, but fall back to baselineSchema if needed
    // Removed fallback to 'output' as currentSchemaView is now a controlled prop
    const effectiveSchemaView = currentSchemaView || baselineSchema;
    let currentDerivedSchema: SchemaProperty[] = [];
    let currentComparison: SchemaComparisonResult | null = null;

    // Determine which transforms to consider based on selection
    let relevantTransforms: PlaybookTransformItem[] = [];
    if (selection.type === 'event' && selection.eventId) {
      const selectedEvent = events.find(e => e.id === selection.eventId);
      if (selectedEvent) {
        // Include global transforms and event-specific transforms for the selected event
        relevantTransforms = transforms.filter(t => 
          t.appliesToScope === 'global' || 
          (t.appliesToScope === 'eventSpecific' && t.eventIds?.includes(selectedEvent.id))
        );
      }
    } else if (selection.type === 'global' || selection.type === 'connection') {
      // When 'Global Transforms' or 'Connection' is selected, show all properties from all transforms
      relevantTransforms = transforms;
    } else {
      // For no specific selection, default to global transforms
      relevantTransforms = transforms.filter(t => t.appliesToScope === 'global');
    }

    // Get the selected event's schema definition for filtering
    let selectedEventSchema: EventSchema | undefined;
    if (selection.type === 'event' && selection.eventId) {
      const selectedEvent = events.find(e => e.id === selection.eventId);
      if (selectedEvent) {
        // Find the corresponding event schema in baselineSchemaEvents
        selectedEventSchema = baselineSchemaEvents.find(eventSchema => 
          eventSchema.codeName === selectedEvent.codeName || 
          eventSchema.name === selectedEvent.name ||
          eventSchema.id === selectedEvent.id
        );
      }
    }

    if (effectiveSchemaView === 'input' && inputSchema) {
      // For input schema view, show the input schema properties and their mapping status
      let inputSchemaProperties: SchemaProperty[] = inputSchema.parameters.map(param => ({
        path: param.fullPath || param.path,
        dataType: param.dataTypes?.[0] || 'string',
        description: param.description || '',
        status: 'missing' as const, // Default to missing, will be updated below
        source: undefined,
        sourceTransformType: undefined,
        sourceTransformId: undefined,
        sourceEventId: undefined,
        sourceInputPath: undefined,
        sourceOutputPath: undefined,
        sourceAppliesToScope: undefined,
        isPII: param.isPii,
        isRequired: param.priorities.includes('Required'), 
        regexFormula: undefined,
      }));

      // Filter properties based on selected event schema if an event is selected
      if (selectedEventSchema && selectedEventSchema.parameterFullPaths) {
        const allowedPaths = new Set(selectedEventSchema.parameterFullPaths);
        inputSchemaProperties = inputSchemaProperties.filter(prop => allowedPaths.has(prop.path));
      }

      // Check which input properties are used by transforms
      inputSchemaProperties.forEach(prop => {
        const mappingTransform = relevantTransforms.find(t => {
          if (t.type === 'mapping') {
            const content = t.content as any;
            return content.inputKey === prop.path;
          } else if (t.type === 'spreading') {
            const content = t.content as any;
            return content.inputKey === prop.path;
          }
          return false;
        });

        if (mappingTransform) {
          if (isTransformProperlyConfigured(mappingTransform)) {
            prop.status = 'mapped';
            prop.source = mappingTransform.name || `${mappingTransform.type}`;
            prop.sourceTransformType = mappingTransform.type;
            prop.sourceTransformId = mappingTransform.id;
            prop.sourceAppliesToScope = mappingTransform.appliesToScope;
            if (mappingTransform.type === 'mapping') {
              const content = mappingTransform.content as any;
              prop.sourceInputPath = prop.path;
              prop.sourceOutputPath = content.outputKey;
            } else if (mappingTransform.type === 'spreading') {
              const content = mappingTransform.content as any;
              prop.sourceInputPath = prop.path;
              prop.sourceOutputPath = content.inferredOutputProperties?.[0]?.path; // Use the first inferred output property for tooltip
            }
          } else {
            // Transform exists but is not properly configured
            prop.status = 'incomplete';
            prop.description = `Used by incomplete ${mappingTransform.type} transform: ${mappingTransform.name || mappingTransform.id}`;
            prop.source = `${mappingTransform.name || mappingTransform.type} (Incomplete)`;
            prop.sourceTransformType = mappingTransform.type;
            prop.sourceTransformId = mappingTransform.id;
            prop.sourceAppliesToScope = mappingTransform.appliesToScope;
            if (mappingTransform.type === 'mapping') {
              const content = mappingTransform.content as any;
              prop.sourceInputPath = prop.path;
              prop.sourceOutputPath = content.outputKey;
            } else if (mappingTransform.type === 'spreading') {
              const content = mappingTransform.content as any;
              prop.sourceInputPath = prop.path;
              prop.sourceOutputPath = content.inferredOutputProperties?.[0]?.path;
            }
          }
        }
      });

      currentDerivedSchema = inputSchemaProperties;

    } else if (effectiveSchemaView === 'output' && outputSchema) {
      // For output schema view, show the output schema properties and their mapping status
      let outputSchemaProperties: SchemaProperty[] = outputSchema.parameters.map(param => ({
        path: param.fullPath || param.path,
        dataType: param.dataTypes?.[0] || 'string',
        description: param.description || '',
        status: 'missing' as const, // Default to missing, will be updated below
        source: undefined,
        sourceTransformType: undefined,
        sourceTransformId: undefined,
        sourceEventId: undefined,
        sourceInputPath: undefined,
        sourceOutputPath: undefined,
        sourceAppliesToScope: undefined,
        isPII: param.isPii, // Pass PII status
        isRequired: param.priorities.includes('Required'),
        regexFormula: undefined,
      }));

      // Filter properties based on selected event schema if an event is selected
      if (selectedEventSchema && selectedEventSchema.parameterFullPaths) {
        const allowedPaths = new Set(selectedEventSchema.parameterFullPaths);
        outputSchemaProperties = outputSchemaProperties.filter(prop => allowedPaths.has(prop.path));
      }

      // Get properties generated by transforms
      const propertiesGeneratedByTransforms = deriveOutputSchema(relevantTransforms);
      
      // Create a map of generated properties for quick lookup
      const generatedPropertiesMap = new Map<string, SchemaProperty>();
      propertiesGeneratedByTransforms.forEach(prop => {
        generatedPropertiesMap.set(prop.path, prop);
      });
      
      // Mark output schema properties as mapped if they're generated by transforms
      outputSchemaProperties.forEach(prop => {
        const generatedProp = generatedPropertiesMap.get(prop.path);
        if (generatedProp) {
          prop.status = generatedProp.status;
          prop.source = generatedProp.source;
          prop.sourceTransformType = generatedProp.sourceTransformType;
          prop.sourceTransformId = generatedProp.sourceTransformId;
          prop.sourceEventId = generatedProp.sourceEventId;
          prop.sourceInputPath = generatedProp.sourceInputPath;
          prop.sourceOutputPath = generatedProp.sourceOutputPath;
          prop.sourceAppliesToScope = generatedProp.sourceAppliesToScope;
          // Do not propagate isPII, isRequired, or regexFormula from generatedProp
          // as these should come from the outputSchema definition itself.
          // The generatedProp primarily indicates mapping status.
        }
      });
      
      // Add extra properties generated by transforms but not in the output schema
      const outputSchemaPaths = new Set(outputSchemaProperties.map(p => p.path));
      const extraProperties = propertiesGeneratedByTransforms.filter(p => !outputSchemaPaths.has(p.path));
      
      // Filter extra properties based on selected event schema if an event is selected
      let filteredExtraProperties = extraProperties;
      if (selectedEventSchema && selectedEventSchema.parameterFullPaths) {
        const allowedPaths = new Set(selectedEventSchema.parameterFullPaths);
        filteredExtraProperties = extraProperties.filter(prop => allowedPaths.has(prop.path));
      }
      
      filteredExtraProperties.forEach(prop => {
        prop.status = 'extra';
        outputSchemaProperties.push(prop);
      });
      
      currentDerivedSchema = outputSchemaProperties;

    } else if (outputSchemaId && outputSchemaData) {
      // Fallback to using outputSchemaData if outputSchema is not available
      const propertiesGeneratedByTransforms = deriveOutputSchema(relevantTransforms);
      currentComparison = enhancedCompareSchemas(propertiesGeneratedByTransforms, outputSchemaData);
      currentDerivedSchema = updateSchemaPropertiesWithComparison(propertiesGeneratedByTransforms, currentComparison);

    } else {
      // Default case: just show properties generated by transforms
      currentDerivedSchema = deriveOutputSchema(relevantTransforms);
    }

    setDerivedSchema(currentDerivedSchema);

  }, [transforms, events, selection, outputSchemaId, outputSchemaData, baselineSchema, inputSchema, outputSchema, currentSchemaView, deriveOutputSchema, baselineSchemaEvents]);
  
  // Convert derived schema to tree parameters
  useEffect(() => {
    if (derivedSchema.length > 0) {
      const parameters = convertToParameters(derivedSchema);
      setTreeParameters(parameters);
    } else {
      setTreeParameters([]);
    }
  }, [derivedSchema]);

  // Remove the local function since we now have an enhanced version in utils

  const toggleTreeNode = useCallback((path: string, _type: 'global' | 'eventSpecific') => {
    setExpandedPaths(prev => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  }, []);

  // Filter tree parameters based on status and search query with proper tree structure handling
  const filteredTreeParameters = useMemo(() => {
    const hasStatusFilter = filterStatus !== 'all';
    const hasSearchFilter = searchQuery.trim().length > 0;
    
    if (!hasStatusFilter && !hasSearchFilter) return treeParameters;
    
    // Helper function to recursively filter tree nodes while preserving structure
    const filterTreeNodes = (nodes: Parameter[]): Parameter[] => {
      const filtered: Parameter[] = [];
      
      for (const node of nodes) {
        const nodeStatus = (node as any).status;
        const hasMatchingChildren = node.children && node.children.length > 0;
        
        // Check if this node matches the status filter
        const statusMatches = !hasStatusFilter || nodeStatus === filterStatus;
        
        // Check if this node matches the search filter (case-insensitive)
        const searchMatches = !hasSearchFilter || 
          node.path.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (node.fullPath && node.fullPath.toLowerCase().includes(searchQuery.toLowerCase()));
        
        // Check if this node matches both filters
        const nodeMatches = statusMatches && searchMatches;
        
        // Recursively filter children
        let filteredChildren: Parameter[] = [];
        if (hasMatchingChildren) {
          filteredChildren = filterTreeNodes(node.children!);
        }
        
        // Include this node if:
        // 1. It matches both filters directly, OR
        // 2. It has children that match the filters (to preserve tree structure)
        if (nodeMatches || filteredChildren.length > 0) {
          filtered.push({
            ...node,
            children: filteredChildren
          });
        }
      }
      
      return filtered;
    };
    
    return filterTreeNodes(treeParameters);
  }, [treeParameters, filterStatus, searchQuery]);

  // Handle transform creation from schema property click
  const handleCreateTransform = useCallback((transformType: 'mapping' | 'enrichment' | 'expression' | 'spreading', propertyPath: string, isInputSchema: boolean) => {
    if (!onAddTransforms) return;

    // Get default content based on transform type
    const getDefaultContent = (type: typeof transformType) => {
      switch (type) {
        case 'mapping':
          return {
            inputKey: isInputSchema ? propertyPath : undefined,
            outputKey: isInputSchema ? undefined : propertyPath,
            transforms: [],
          };
        case 'enrichment':
          return {
            type: 'UUID',
            outputKey: propertyPath,
          };
        case 'expression':
          return {
            lang: 'lua',
            body: '',
            declaredOutputProperties: isInputSchema ? [] : [{ path: propertyPath, dataType: 'string' }],
          };
        case 'spreading':
          return {
            inputKey: isInputSchema ? propertyPath : undefined,
            prefix: '',
            separator: '.',
            depth: 1,
            arrayStartIndex: 0,
            inferredOutputProperties: isInputSchema ? [] : [{ path: propertyPath, dataType: 'string' }],
          };
        default:
          return {};
      }
    };

    const newTransform: PlaybookTransformItem = {
      id: `transform_${Date.now()}`,
      type: transformType,
      content: getDefaultContent(transformType),
      appliesToScope: 'global', // Always create a global transform when initiated from a property click
      eventIds: undefined, // Global transforms don't apply to specific events
      isNew: true,
    };

    onAddTransforms([newTransform]);
  }, [onAddTransforms]); // Removed 'selection' from dependency array as it's no longer used for appliesToScope

  // Animation variants for responsive behavior
  const variants = {
    expanded: { opacity: 1, x: 0, pointerEvents: 'auto' as const },
    collapsed: { opacity: 1, x: width, pointerEvents: 'none' as const },
  };

  const getWidthStyle = () => {
    // On small screens, allow the panel to take full width, but respect the minWidth.
    // On large screens, use the provided 'width' prop.
    if (!isLargeDesktop) {
      return {
        width: '100%', // Take full width of parent
      };
    }
    return {
      width: `${width}px`,
      minWidth: '320px', // Minimum usable width for desktop
    };
  };

  return (
    <motion.div // Add background classes here
      className="h-full flex flex-col relative bg-white dark:bg-gray-800 flex-shrink"
      initial="expanded"
      animate={!isLargeDesktop && isCollapsed ? "collapsed" : "expanded"}
      variants={variants}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      style={getWidthStyle()}
    >

      {/* Mapping Progress Bar */}
      {mappingProgress && mappingProgress.totalCount > 0 && (
        <div className="px-4 py-6 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <MappingProgressBar
            mappedCount={mappingProgress.mappedCount}
            totalCount={mappingProgress.totalCount}
            requiredMappedCount={mappingProgress.requiredMappedCount}
            requiredTotalCount={mappingProgress.requiredTotalCount}
            missingCount={mappingProgress.missingCount}
            extraCount={mappingProgress.extraCount}
            mismatchCount={mappingProgress.mismatchCount}
          />
        </div>
      )}

      {/* Header with Schema View Toggle */}
      <div className="px-4 pt-5 pb-2 border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center space-x-2 flex-grow min-w-0">
              {currentSchemaView === 'yaml' ? (
                <Listbox value={currentYamlView} onChange={setCurrentYamlView}>
                  {({ open: _open }) => (
                    <div className="relative w-full">
                      <ListboxButton className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 dark:focus:border-blue-600 focus:ring-blue-500 focus:border-blue-500 rounded-md focus:ring focus:outline-none dark:focus:ring-blue-500/20 dark:bg-gray-800 dark:focus:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition duration-300 ease-in-out relative cursor-default text-left sm:text-sm sm:leading-6">
                        <span className="block truncate mr-4">
                          {currentYamlView === 'playbook' ? 'playbook.yaml' : 'connection.yaml'}
                        </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.75 9.75a.75.75 0 011.1 0L10 15.148l2.7-2.908a.75.75 0 011.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 010-1.02z" clipRule="evenodd" />
                        </svg>
                      </span>
                    </ListboxButton>
                    <ListboxOptions
                      transition
                      className="absolute z-30 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in sm:text-sm"
                    >
                      <ListboxOption
                        className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-blue-600 data-[focus]:text-white dark:text-white dark:data-[focus]:bg-blue-600"
                        value="playbook"
                      >
                        <span className="block truncate font-normal group-data-[selected]:font-semibold">
                          Playbook YAML
                        </span>
                        {currentYamlView === 'playbook' && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 group-data-[focus]:text-white">
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </ListboxOption>
                      {state.type === 'integration' && ( // Conditionally render Connection YAML option
                        <ListboxOption
                          className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-blue-600 data-[focus]:text-white dark:text-white dark:data-[focus]:bg-blue-600"
                          value="connection"
                        >
                          <span className="block truncate font-normal group-data-[selected]:font-semibold">
                            Connection YAML
                          </span>
                          {currentYamlView === 'connection' && (
                            <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 group-data-[focus]:text-white">
                              <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                              </svg>
                            </span>
                          )}
                        </ListboxOption>
                      )}
                    </ListboxOptions>
                  </div>
                )}
              </Listbox>
            ) : (
              <span className="font-medium text-gray-700 dark:text-gray-300 truncate">
                {currentSchemaView === 'input' && inputSchema
                  ? inputSchema.name
                  : currentSchemaView === 'output' && outputSchema
                  ? outputSchema.name
                  : 'Schema Preview'
                }
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Schema View Toggle - Always show when both schemas are available */}
            {((inputSchema && outputSchema) || (inputSchemaId || outputSchemaId)) && (
              <div className="flex flex-nowrap bg-gray-200 dark:bg-gray-900 rounded-md p-1">
                {(inputSchema || inputSchemaId) && (
                  <button
                    onClick={() => setCurrentSchemaView('input')}
                    className={`px-2 py-1 text-xs rounded transition-colors relative ${
                      currentSchemaView === 'input'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex flex-nowrap items-center">
                      <span className="underline">I</span>nput{baselineSchema === 'input' && (
                        <span className="inline-block ml-1 h-1.5 w-1.5 rounded-full bg-blue-500 align-middle" title="Baseline Schema"></span>
                      )}
                    </span>
                  </button>
                )}
                {(outputSchema || outputSchemaId) && (
                  <button
                    onClick={() => setCurrentSchemaView('output')}
                    className={`px-2 py-1 text-xs rounded transition-colors relative ${
                      currentSchemaView === 'output'
                        ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                        : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    <span className="flex flex-nowrap items-center">
                      <span className="underline">O</span>utput{baselineSchema === 'output' && (
                        <span className="inline-block ml-1 h-1.5 w-1.5 rounded-full bg-blue-500 align-middle" title="Baseline Schema"></span>
                      )}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setCurrentSchemaView('yaml')}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    currentSchemaView === 'yaml'
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  <span className="underline">Y</span>AML
                </button>
              </div>
            )}
          </div>
        </div>
        
        {currentSchemaView !== 'yaml' && (
          <div className="flex items-center space-x-2">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search properties..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-10 py-1.5 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-800 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:placeholder-gray-400 dark:focus:placeholder-gray-300 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 sm:text-sm text-gray-900 dark:text-gray-100"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="h-4 w-4 text-gray-400" />
                </button>
              )}
            </div>
            <Listbox value={filterStatus} onChange={setFilterStatus}>
              {({ open: _open }) => (
                <div className="relative flex-shrink-0">
                  <ListboxButton className="relative w-full cursor-pointer rounded-md bg-white dark:bg-gray-800 py-1.5 pl-3 pr-10 text-left text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6 min-w-[140px] hover:bg-gray-50 dark:hover:bg-gray-700">
                    <span className="flex items-center">
                      <span className="ml-3 block truncate">
                        {(() => {
                          const filterOptions = [
                            { key: 'all', label: 'All', count: derivedSchema.length },
                            { key: 'mapped', label: 'Mapped', count: getFilterCount('mapped') },
                            { key: 'missing', label: 'Missing', count: getFilterCount('missing') },
                            { key: 'incomplete', label: 'Incomplete', count: getFilterCount('incomplete') },
                            { key: 'extra', label: 'Extra', count: getFilterCount('extra') },
                            { key: 'mismatch', label: 'Type Mismatch', count: getFilterCount('mismatch') },
                          ];
                          const currentFilter = filterOptions.find(f => f.key === filterStatus);
                          return `${currentFilter?.label || 'All'} (${currentFilter?.count || 0})`;
                        })()}
                      </span>
                    </span>
                    <span className="pointer-events-none absolute inset-y-0 right-0 ml-3 flex items-center pr-2">
                      <ChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    </span>
                  </ListboxButton>

                  <ListboxOptions
                    transition
                    className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white dark:bg-gray-800 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in sm:text-sm"
                  >
                    {[
                      { key: 'all', label: 'All', count: derivedSchema.length },
                      { key: 'mapped', label: 'Mapped', count: getFilterCount('mapped') },
                      { key: 'missing', label: 'Missing', count: getFilterCount('missing') },
                      { key: 'incomplete', label: 'Incomplete', count: getFilterCount('incomplete') },
                      { key: 'extra', label: 'Extra', count: getFilterCount('extra') },
                      { key: 'mismatch', label: 'Type Mismatch', count: getFilterCount('mismatch') },
                    ].map(({ key, label, count }) => (
                      <ListboxOption
                        key={key}
                        className="group relative cursor-default select-none py-2 pl-3 pr-9 text-gray-900 dark:text-gray-100 data-[focus]:bg-blue-600 data-[focus]:text-white"
                        value={key}
                      >
                        <div className="flex items-center">
                          <span className="ml-3 block truncate font-normal group-data-[selected]:font-semibold">
                            {label} ({count})
                          </span>
                        </div>

                        {filterStatus === key && (
                          <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-blue-600 group-data-[focus]:text-white">
                            <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clipRule="evenodd" />
                            </svg>
                          </span>
                        )}
                      </ListboxOption>
                    ))}
                  </ListboxOptions>
                </div>
              )}
            </Listbox>
          </div>
        )}
      </div>

      {/* Schema Tree / YAML Preview */}
      <div className="flex-1 overflow-auto p-3 pt-0">
       {currentSchemaView === 'yaml' ? (
         <div className="h-full">
           <YamlCodeViewer
             value={generatedYaml}
           />
         </div>
       ) : treeParameters.length === 0 ? (
          <div className="text-center text-gray-500 dark:text-gray-400 px-4 py-8">
            <div className="text-sm">
              {derivedSchema.length === 0
                ? baselineSchema
                  ? `No ${baselineSchema} schema loaded. Please select a schema in the Details tab.`
                  : 'No properties derived yet. Add transforms to see the output schema.'
                : `No ${filterStatus} properties found.`
              }
            </div>
          </div>
        ) : (
          <SchemaTreeView
            parameters={filteredTreeParameters}
            toggleTreeNode={toggleTreeNode}
            expandedPaths={expandedPaths}
            allEvents={baselineSchemaEvents}
            selectedEventId={selection.type === 'event' ? selection.eventId : undefined}
            onPropertyClick={(property) => { // Modify onPropertyClick to handle SchemaProperty
              if (property.sourceTransformId) {
                // If a transform exists, select it
                onTransformSelect(property.sourceTransformId);
              } else if (handleCreateTransform) { // Corrected to handleCreateTransform
                // If no transform exists, create a new mapping transform
                handleCreateTransform('mapping', property.path, currentSchemaView === 'input'); // Corrected to property.path
              }
            }}
            onCreateTransform={handleCreateTransform} // Pass transform creation handler
            currentSchemaView={currentSchemaView} // Pass current schema view
            selectedSection={selection.type} // Pass selected section
          />
        )}
      </div>

      {/* Resize handle - only show on larger screens */}
      {isLargeDesktop && (
        <motion.div
          className="absolute left-0 top-0 bottom-0 w-2 group hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors duration-150"
          style={{ cursor: 'col-resize', userSelect: 'none' }} // Apply col-resize cursor and prevent text selection
          onPanStart={(e, _info) => { 
            e.preventDefault(); // Prevent default text selection behavior
            dragControls.start(e); 
          }}
          onPan={(_, info) => {
            // Prevent shrinking below 640px on extra large screens (isLargeDesktop)
            const minAllowedWidth = isLargeDesktop ? 640 : 320;
            const newWidth = Math.max(minAllowedWidth, Math.min(640, width - info.delta.x));
            setWidth(newWidth); // Use the setWidth prop
          }}
        >
          <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gray-200 dark:bg-gray-700 group-hover:bg-blue-400 dark:group-hover:bg-blue-500 transition-colors duration-150" />
        </motion.div>
      )}

    </motion.div>
  );
};

export default PlaybookSchemaPreviewPanel;
